<template lang="pug">
.other-office-facilities-table-component
  // テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
  )
    //- ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    //- レコード無し
    template(#empty)
      .empty-message
        span 情報がありません
    //- フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません

    //- セル：事業所名
    template(#cell(name)="row")
      span {{ row.item.facility.name }}
    //- セル：地域
    template(#cell(reason)="row")
      span {{ getReason(row.item) }}
    //- セル：施設種別
    template(#cell(type)="row")
      span {{ convertTypeLabel(row.item) }}
    //- セル：電話番号
    template(#cell(tel)="row")
      span {{ row.item.facility.tel }}
    //- セル：ホームページ
    template(#cell(hpUrl)="row")
      a.link(:href="row.item.facility.url" target="_blank")
        span.link-text(style="margin:0;") {{ row.item.facility.url }}
    //- セル：詳細
    template(#cell(detail)="row")
      button.btn.detail-btn.mr-2(@click="onDetail(row.item)") 詳細
      button.btn.application-btn(@click="onApplication(row.item)" :class="{ 'disable': disableApplication(row.item) }" :disabled="disableApplication(row.item)") 入所申込み
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";

import { OtherOfficeConnectionGetModel } from "@/commons/services/OtherOfficeConnections";
import { CareLevels, FacilityCategory, MedicalNursings } from "@/commons/services/Facilities";

/**
 * ### 連携施設テーブルコンポーネント
 */
@Component({})
export default class OtherOfficeFacilitiesTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<OtherOfficeFacilitiesTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<OtherOfficeFacilitiesTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;
  /** フィルター */
  @Prop()
  filter!: FilterSetting;

  get _value(): Array<OtherOfficeFacilitiesTableItem> {
    return this.value;
  }

  getReason(item: OtherOfficeFacilitiesTableItem): string {
    if (!item.facility || !item.facility.facilityDetailInfo || !item.facility.facilityDetailInfo.addressInfo) {
      return "";
    }
    const addressInfo = item.facility.facilityDetailInfo.addressInfo;
    return `${addressInfo.prefecture}${addressInfo.municipality}`;
  }

  convertTypeLabel(item: OtherOfficeFacilitiesTableItem): string {
    if (!item.facility || !item.facility.facilityDetailInfo || !item.facility.facilityDetailInfo.facilityCategory) {
      return "";
    }

    switch (item.facility.facilityDetailInfo.facilityCategory) {
      /** 介護付有料老人ホーム */
      case FacilityCategory.KAIGO_YUURYOU: {
        return "介護付有料老人ホーム";
      }
      /** 住宅型有料老人ホーム */
      case FacilityCategory.JYUUTAKU_YUURYOU: {
        return "住宅型有料老人ホーム";
      }
      /** サービス付き高齢者向け住宅 */
      case FacilityCategory.SERVICE_JYUUTAKU: {
        return "サービス付き高齢者向け住宅";
      }
      /** シニア向け分譲マンション */
      case FacilityCategory.CONDOMINIUMS: {
        return "シニア向け分譲マンション";
      }
      /** グループホーム */
      case FacilityCategory.GROUP_HOME: {
        return "グループホーム";
      }
      /** ケアハウス */
      case FacilityCategory.CARE_HOUSE: {
        return "ケアハウス";
      }
      /** 特別養護老人ホーム（特養） */
      case FacilityCategory.TOKUYO: {
        return "特別養護老人ホーム（特養）";
      }
      /** 介護老人保健施設（老健） */
      case FacilityCategory.KAIGO_HOKEN: {
        return "介護老人保健施設（老健）";
      }
      /** 介護医療院 */
      case FacilityCategory.MEDICAL_CENTER: {
        return "介護医療院";
      }
      /** その他 */
      case FacilityCategory.OTHER: {
        return "その他";
      }
    }
  }

  disableApplication(item: OtherOfficeFacilitiesTableItem): boolean {
    if (!item.facility || !item.facility.useWaiterService) {
      return true;
    }
    return false;
  }

  /**
   * ### フィルタリング
   */
  public filterTable(item: OtherOfficeFacilitiesTableItem, filter: FilterSetting): boolean {
    return (
      this.filteringRegion(item, filter) &&
      this.filteringFacilityCategory(item, filter) &&
      this.filteringCareLevels(item, filter) &&
      this.filterOnlyAcceptsWelfare(item, filter) &&
      this.filterMedicalNursings(item, filter) &&
      this.filterOfficeName(item, filter)
    );
  }

  /**
   * ### 地域情報フィルタリング
   */
  private filteringRegion(item: OtherOfficeFacilitiesTableItem, filter: FilterSetting): boolean {
    // フィルタリングの地域が空文字（全国）なら全てを返す
    if (!filter.prefecture) {
      return true;
    }

    // 地域情報がない場合は非表示で扱う
    if (!item.facility.facilityDetailInfo || !item.facility.facilityDetailInfo.region) {
      return false;
    }

    // 都道府県でフィルタリング
    if (item.facility.facilityDetailInfo.region.prefectureCode !== filter.prefecture) {
      return false;
    }

    // 市区町村でフィルタリング
    const selectedMunicipalities = filter.municipalities[filter.prefecture];
    // 市区町村が選択されていない、または配列が空の場合は、都道府県のフィルタリング結果のみを返す
    if (!selectedMunicipalities || selectedMunicipalities.length === 0) {
      return true;
    }

    // 市区町村が選択されている場合、施設情報の市区町村コードが選択されているかを返す
    return selectedMunicipalities.includes(item.facility.facilityDetailInfo.region.municipalityCode);
  }

  /**
   * ### 施設種別フィルタリング
   */
  private filteringFacilityCategory(item: OtherOfficeFacilitiesTableItem, filter: FilterSetting): boolean {
    // フィルタリングの施設種別が空文字（全て）なら全てを返す
    if (!filter.facilityCategory) {
      return true;
    }

    // 施設種別がない場合は非表示で扱う
    if (!item.facility.facilityDetailInfo || !item.facility.facilityDetailInfo.facilityCategory) {
      return false;
    }

    // 施設種別でフィルタリング
    if (item.facility.facilityDetailInfo.facilityCategory !== filter.facilityCategory) {
      return false;
    }

    return true;
  }

  /**
   * ### 介護度フィルタリング
   */
  private filteringCareLevels(item: OtherOfficeFacilitiesTableItem, filter: FilterSetting): boolean {
    // フィルタリングの介護度が全てfalseなら全てを返す
    let exist: boolean = false;
    for (const careLevel of Object.values(filter.careLevels)) {
      if (careLevel) {
        exist = true;
        break;
      }
    }
    if (!exist) {
      return true;
    }

    // 介護度がない場合は非表示で扱う
    if (!item.facility.facilityDetailAccept || !item.facility.facilityDetailAccept.careLevels) {
      return false;
    }

    // 介護度でフィルタリング
    // フィルタの介護度がtrue かつ 施設の受け入れ可能な介護度の同名プロパティがtrueであればtrueを返す
    for (const key of Object.keys(filter.careLevels)) {
      const careLevelKey = key as keyof CareLevels;
      if (filter.careLevels[careLevelKey] && item.facility.facilityDetailAccept.careLevels[careLevelKey]) {
        return true;
      }
    }

    return false;
  }

  private filterOnlyAcceptsWelfare(item: OtherOfficeFacilitiesTableItem, filter: FilterSetting): boolean {
    // 受け入れ可 施設のみでなければ全てを返す
    if (!filter.acceptsWelfare) {
      return true;
    }

    // 施設の追加情報が無ければ非表示対象
    if (!item.facility.facilityDetailAccept) {
      return false;
    }
    // 施設の追加情報があれば 生活保護を受け入れるか否か の値を返す
    else {
      if (item.facility.facilityDetailAccept.acceptsWelfare) {
        return true;
      } else {
        return false;
      }
    }
  }

  /**
   * ### 医療・看護体制フィルタリング
   */
  private filterMedicalNursings(item: OtherOfficeFacilitiesTableItem, filter: FilterSetting): boolean {
    // フィルタリングの医療・看護体制が全てfalseなら全てを返す
    let exist: boolean = false;
    for (const medicalNursing of Object.values(filter.medicalNursings)) {
      if (medicalNursing) {
        exist = true;
        break;
      }
    }
    if (!exist) {
      return true;
    }

    // 医療・看護体制がない場合は非表示で扱う
    if (!item.facility.facilityDetailAccept || !item.facility.facilityDetailAccept.medicalNursings) {
      return false;
    }

    // 医療・看護体制でフィルタリング
    // フィルタの介護度がtrue かつ 施設の受け入れ可能な介護度の同名プロパティがtrueであればtrueを返す
    for (const key of Object.keys(filter.medicalNursings)) {
      const medicalNursingKey = key as keyof MedicalNursings;
      if (filter.medicalNursings[medicalNursingKey] && item.facility.facilityDetailAccept.medicalNursings[medicalNursingKey]) {
        return true;
      }
    }

    return false;
  }

  private filterOfficeName(row: OtherOfficeFacilitiesTableItem, filter: FilterSetting): boolean {
    if (filter.officeName == "") {
      return true;
    }

    const words: string[] = this.$filterUtil.createSearchWord(filter.officeName);

    let foundOfficeName: boolean = true;

    for (const word of words) {
      // 事業所名
      if (row.officeName.indexOf(word) == -1) {
        foundOfficeName = false;
      }
    }

    return foundOfficeName;
  }

  /**
   * ### フィルタリングイベント
   */
  onFiltered(filteredItems: any) {
    this.$emit("update:totalRows", filteredItems.length);
    return;
  }

  /**
   * ### 詳細ボタン押下イベント
   */
  onDetail(item: OtherOfficeFacilitiesTableItem): void {
    this.$emit("onDetail", item);
    return;
  }

  /**
   * ### 入所申込みボタン押下イベント
   */
  async onApplication(item: OtherOfficeFacilitiesTableItem): Promise<void> {
    this.$emit("onApplication", item);
    return;
  }
}

/** テーブルの列名の情報 */
export interface OtherOfficeFacilitiesTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface OtherOfficeFacilitiesTableItem extends OtherOfficeConnectionGetModel {}

export interface FilterSetting {
  /** 地域（都道府県） */
  prefecture: string;
  /** 地域（市区町村） */
  municipalities: { [key: string]: string[] };
  /** 施設種別 */
  facilityCategory: FacilityCategory | "";
  /** 介護度（複数選択可能） */
  careLevels: CareLevels;
  /** 生活保護を受け入れるか否か */
  acceptsWelfare: boolean;
  /** 医療・看護体制 */
  medicalNursings: MedicalNursings;
  /** 事業所名 */
  officeName: string;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

.other-office-connection-table-component {
  .detail-btn {
    width: 94px;
  }
  .application-btn {
    width: 124px;
  }
}
</style>
